/**
 * Professional Error Boundary Component
 * Catches JavaScript errors and displays professional error messages
 */

import React, { Component, ReactNode } from 'react';
import { ErrorDetails, errorHandler } from '../utils/errorHandler';

interface Props {
  children: ReactNode;
  fallback?: (error: ErrorDetails, retry: () => void) => ReactNode;
  onError?: (error: ErrorDetails) => void;
}

interface State {
  hasError: boolean;
  errorDetails: ErrorDetails | null;
  errorId: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorDetails: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    const errorDetails = errorHandler.handleError(error, {
      boundary: 'ErrorBoundary',
      timestamp: new Date().toISOString()
    });

    return {
      hasError: true,
      errorDetails,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const errorDetails = errorHandler.handleError(error, {
      boundary: 'ErrorBoundary',
      componentStack: errorInfo.componentStack,
      errorBoundary: true
    });

    // Call onError callback if provided
    if (this.props.onError) {
      this.props.onError(errorDetails);
    }

    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  retry = () => {
    this.setState({
      hasError: false,
      errorDetails: null,
      errorId: null
    });
  };

  render() {
    if (this.state.hasError && this.state.errorDetails) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.errorDetails, this.retry);
      }

      // Default professional error UI
      return (
        <div className="professional-error-boundary">
          <div className="professional-error-boundary__container">
            <div className="professional-error-boundary__icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            
            <div className="professional-error-boundary__content">
              <h2 className="professional-error-boundary__title">
                Something went wrong
              </h2>
              
              <p className="professional-error-boundary__message">
                {this.state.errorDetails.userMessage}
              </p>
              
              <div className="professional-error-boundary__details">
                <p className="professional-error-boundary__error-id">
                  Error ID: {this.state.errorId}
                </p>
                
                {this.state.errorDetails.severity === 'critical' && (
                  <p className="professional-error-boundary__support">
                    Our technical team has been automatically notified. 
                    If you continue to experience issues, please contact support with the error ID above.
                  </p>
                )}
              </div>
              
              <div className="professional-error-boundary__actions">
                <button
                  className="professional-btn professional-btn--primary"
                  onClick={this.retry}
                >
                  Try Again
                </button>
                
                <button
                  className="professional-btn professional-btn--secondary"
                  onClick={() => window.location.reload()}
                >
                  Reload Page
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook for handling errors in functional components
 */
export const useErrorHandler = () => {
  const handleError = React.useCallback((error: any, context?: Record<string, any>) => {
    const errorDetails = errorHandler.handleError(error, context);
    
    // In a real app, you might want to show a toast notification
    // or update some global error state here
    console.error('Error handled:', errorDetails);
    
    return errorDetails;
  }, []);

  return { handleError };
};